import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../../redux/Store';
import axios from 'axios';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { additionalQueryParams } from '../../utils/utilities';
import { setModalContent, setShowModal } from '../utils/utils.slice';
import {
  PortRequestType,
  PortRequestLog,
  CreatePortRequestDto,
  CheckPortabilityDto,
  CheckPortabilityType,
  BandwidthPortHistoryResponse,
} from '../../types/port-request/port-request.type';
import { rejectWithApiError } from '../../utils/api';
import { RootReducerState } from '../../redux/Reducers';

export const postPortingDetails = createAsyncThunk<
  PortRequestType,
  {
    data: CreatePortRequestDto;
    files: File[];
  },
  { state: RootReducerState }
>('portings/postPortingDetails', async ({ data, files }, thunkAPI) => {
  try {
    const formData = new FormData();
    formData.append('body', JSON.stringify({ ...data }));
    files.forEach(file => {
      formData.append('files', file);
    });

    const response = await axios.post<PortRequestType>(`${process.env.REACT_APP_API_URL}/port-request`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating port request!' });
  }
});

export const postCheckPortability = createAsyncThunk<
  CheckPortabilityType,
  { data: CheckPortabilityDto },
  { state: RootReducerState }
>('portings/postCheckPortability', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<CheckPortabilityType>(
      `${process.env.REACT_APP_API_URL}/port-request/bandwidth/check-portability`,
      data
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during checking portability!' });
  }
});

export const getPortingsList = createAsyncThunk<
  PaginationType<PortRequestType>,
  RouteParamsType & {
    controller: AbortController;
  },
  { state: RootState }
>('portings/getPortingsList/no-loader', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;

    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      thunkAPI.getState().sharedReducer.selectedLocation,
      thunkAPI.getState().sharedReducer.includeSuborgs,
      thunkAPI.getState().sharedReducer.selectedCountry
    );
    const mergedParams = { ...params, ...additionalParams };
    const queryString = Object.keys(mergedParams)
      .map(key => key + '=' + mergedParams[key])
      .join('&');

    const response = await axios.get<PaginationType<PortRequestType>>(
      `${process.env.REACT_APP_API_URL}/port-request${queryString ? `?${queryString}` : ''}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching port requests!' });
  }
});

export const getPortingDetails = createAsyncThunk<PortRequestType, { id: number }, { state: RootState }>(
  'portings/getPortingDetails',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<PortRequestType>(`${process.env.REACT_APP_API_URL}/port-request/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching port request!' });
    }
  }
);

export const patchPortingDetails = createAsyncThunk<
  PortRequestType,
  { id: number; data: unknown },
  {
    state: RootState;
  }
>('portings/patchPortingDetails', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<PortRequestType>(`${process.env.REACT_APP_API_URL}/port-request/${id}`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating port request!' });
  }
});

export const archivePortingDetails = createAsyncThunk<
  PortRequestType,
  { id: number; fetchList: boolean },
  { state: RootState }
>('portings/archivePortingDetails', async ({ id, fetchList }, thunkAPI) => {
  try {
    const response = await axios.delete<PortRequestType>(`${process.env.REACT_APP_API_URL}/port-request/${id}`);
    if (fetchList) thunkAPI.dispatch(getPortingsList(thunkAPI.getState().portingsReducer.portingsRouteParams));
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during archiving port request!' });
  }
});

export const cancelPortingDetails = createAsyncThunk<PortRequestType, { id: number }, { state: RootState }>(
  'portings/cancelPortingDetails',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.delete<PortRequestType>(
        `${process.env.REACT_APP_API_URL}/port-request/cancel/${id}`
      );
      thunkAPI.dispatch(getPortingDetails({ id }));
      thunkAPI.dispatch(setShowModal(false));
      thunkAPI.dispatch(setModalContent(null));
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during canceling port request!' });
    }
  }
);

export const completePortingDetails = createAsyncThunk<PortRequestType, { id: number }, { state: RootState }>(
  'portings/completePortingDetails',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.patch<PortRequestType>(
        `${process.env.REACT_APP_API_URL}/port-request/complete/${id}`
      );
      thunkAPI.dispatch(getPortingDetails({ id }));
      thunkAPI.dispatch(setShowModal(false));
      thunkAPI.dispatch(setModalContent(null));
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during completing port request!' });
    }
  }
);

export const getPortRequestLogs = createAsyncThunk('portings/getLogs', async (portRequestId: number, thunkAPI) => {
  try {
    const response = await axios.get<{ items: PortRequestLog[] }>(
      `${process.env.REACT_APP_API_URL}/port-request/${portRequestId}/logs`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching port request logs!' });
  }
});

export const getPortRequestHistory = createAsyncThunk<
  BandwidthPortHistoryResponse,
  { id: number },
  { state: RootState }
>('portings/getPortRequestHistory', async ({ id }, thunkAPI) => {
  try {
    const response = await axios.get<BandwidthPortHistoryResponse>(
      `${process.env.REACT_APP_API_URL}/port-request/${id}/history`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching port request history!' });
  }
});

export const remediatePortRequest = createAsyncThunk<
  {
    mode: string;
    portId: number;
    results: Array<{
      portId?: number;
      did: string;
      action: string;
      tempDid?: string | null;
      reason?: string;
    }>;
    summary: { total: number; succeeded: number; failed: number; skipped: number; dryRun: number };
  },
  { portId: number; execute?: boolean; fetchDetails?: boolean },
  { state: RootState }
>('portings/remediatePortRequest', async ({ portId, execute = false, fetchDetails = true }, thunkAPI) => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/port-requests/remediation/fix-port/${portId}?execute=${execute}`
    );
    if (fetchDetails) {
      thunkAPI.dispatch(getPortingDetails({ id: portId }));
    }
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during port request remediation!' });
  }
});
